import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import logo480 from '@/assets/kitchenflow-logo-480.png';
import logo640 from '@/assets/kitchenflow-logo-640.png';
import logo960 from '@/assets/kitchenflow-logo-960.png';

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  if (user) {
    navigate('/', { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const finalEmail = email.includes('@') ? email : `${email}@kitchenflow.com`;
    const { error } = await signIn(finalEmail, password);
    setSubmitting(false);

    if (error) {
      toast({
        title: t('login.loginFailed'),
        description: error.message,
        variant: 'destructive',
      });
    } else {
      navigate('/', { replace: true });
    }
  };

  return (
    <main className="min-h-screen bg-background flex items-center justify-center p-6 relative">
      <div className="absolute right-4 z-20" style={{ top: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}>
        <LanguageSwitcher />
      </div>
      <div className="max-w-sm md:max-w-lg w-full space-y-4 md:space-y-6">
        <div className="text-center space-y-2 md:space-y-4">
          <img
            alt="KitchenFlow"
            className="h-48 md:h-60 object-contain mx-auto"
            width={960}
            height={480}
            decoding="async"
            fetchPriority="high"
            src={logo480}
            srcSet={`${logo480} 480w, ${logo640} 640w, ${logo960} 960w`}
            sizes="(min-width: 768px) 480px, 364px"
          />
          <p className="text-muted-foreground md:text-lg">{t('login.signInToContinue')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="md:text-lg">{t('login.username')}</Label>
            <Input
              id="email"
              type="text"
              placeholder={t('login.usernamePlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              className="md:h-14 md:text-lg"
            />
            
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="md:text-lg">{t('login.password')}</Label>
            <Input
              id="password"
              type="password"
              placeholder={t('login.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="md:h-14 md:text-lg"
            />
          </div>
          <Button type="submit" className="w-full md:h-14 md:text-lg mt-8 md:mt-10" size="lg" disabled={submitting}>
            {submitting ? t('login.signingIn') : t('login.signIn')}
          </Button>
        </form>
      </div>
    </main>
  );
}
